/**
 * AuthenticatorChallengeFlow — the login page's push-2FA screen.
 *
 * Renders the left-hand panel of `CHALLENGE_FLOW.md`'s diagram: the 2-digit number the user must
 * type into PalmPuffin, a waiting state, and — after 10 seconds without a response — the
 * "Use code instead" fallback `SECURITY_MODEL.md` specifies.
 *
 * Sits beside `PasskeyAuthFlow` as the other device-mediated login surface, and follows its shape:
 * strings through `useLoginTranslate`, shared utility classes only, no local stylesheet.
 *
 * ## 🔴 The number is NOT a secret, and the copy has to make that clear
 *
 * Number matching exists so a user who did NOT initiate a sign-in sees a number they cannot
 * possibly have, and refuses. A prompt that reads like "here is your code" invites the user to
 * approve whatever their phone shows — which is exactly the blind approval the control prevents.
 * The copy therefore says "enter this number in PalmPuffin", never "your code is".
 *
 * @module @puffin-core/login-shell/components/AuthenticatorChallengeFlow
 */

import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
// The NARROW subpath, never the barrel: login-shell is router-free (see ContactManagerModal).
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';
import { AuthenticatorCodeEntry } from './AuthenticatorCodeEntry.jsx';
import { AuthenticatorAltActions } from './AuthenticatorAltActions.jsx';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';
import { useAuthenticatorChallenge, CHALLENGE_STATE } from '../hooks/useAuthenticatorChallenge.js';

/**
 * @param {Object} props
 * @param {string} props.challengeId
 * @param {number} props.numberMatch - The 2-digit value (10–99) the user echoes in PalmPuffin.
 * @param {Function} props.pollStatus - `async (challengeId) => { status, ... }`.
 * @param {Function} props.onApproved - `(payload) => void`; the payload carries the server's tokens.
 * @param {Function} [props.onDenied]
 * @param {Function} [props.onExpired]
 * @param {Function} [props.onUseCode] - Hands the user to the app's EXISTING MFA screen. Supplied
 *   only when this login actually carries an `mfaToken`.
 * @param {Function} [props.submitTotpCode] - `async (challengeId, code) => payload`. Supplied when
 *   the host mounts the TOTP fallback route; enables in-place code entry for a push-only user.
 * @param {Function} [props.onCancel]
 * @param {Function} [props.t]
 * @returns {React.JSX.Element}
 */
export function AuthenticatorChallengeFlow({
  challengeId,
  numberMatch,
  pollStatus,
  onApproved,
  onDenied,
  onExpired,
  onUseCode,
  submitTotpCode,
  onCancel,
  t,
}) {
  // Q-D-TOTP-VERIFY: which screen is showing. The code form owns its OWN state (`code`, `error`,
  // `submitting`) in `<AuthenticatorCodeEntry>` — keeping it here took this component to 4 state
  // areas and 6 conditional renders, which `web/render-method-complexity` reports at ERROR.
  const [codeMode, setCodeMode] = useState(false);

  // ⚠️ `web/component-state-order`: the COMPUTED hooks (`useCallback`) come before every CUSTOM
  // hook — and `useAuthenticatorChallenge` is a custom hook too, not just `useLoginTranslate`.
  // Neither callback depends on the challenge, so ordering costs nothing here.
  const handleBackToPush = useCallback(() => setCodeMode(false), []);

  const handleUseCode = useCallback(() => {
    // Prefer the app's real MFA screen when this login has one; otherwise verify in place.
    if (onUseCode) return onUseCode();
    return setCodeMode(true);
  }, [onUseCode]);

  const { status, showFallback, error } = useAuthenticatorChallenge({
    challengeId,
    pollStatus,
    onApproved,
    onResolved: (resolved) => {
      if (resolved === CHALLENGE_STATE.DENIED && onDenied) onDenied();
      if (resolved === CHALLENGE_STATE.EXPIRED && onExpired) onExpired();
    },
  });

  // ⚠️ RECIPE §3: `useLoginTranslate` is a CUSTOM hook and must follow the computed ones
  // (`web/component-state-order`).
  const translate = useLoginTranslate(t);

  const isResolved = status !== CHALLENGE_STATE.PENDING;

  // A terminal status is an ERROR to the user (they cannot proceed), and a transient poll failure
  // is a warning. Both render through the same alert so the screen never has two competing
  // messages — but the terminal one wins, because it is the one that ends the attempt.
  // `error` is the failed poll's catalog leaf (never its message), translated here.
  const message = isResolved
    ? translate(`authenticator.status.${status}`)
    : error
      ? translate(error)
      : null;

  // 🔴 The affordance appears only when a fallback EXISTS. Q-AW-7: it used to render on the mere
  // presence of `onUseCode`, which the factory always supplied because it cannot know at
  // config-build time whether THIS login carried an `mfaToken` — so a push-only user was offered
  // "Use a code instead" and, on clicking it, was returned to the sign-in form with no
  // explanation. A dead affordance is worse than no affordance.
  const hasFallback = Boolean(onUseCode || submitTotpCode);

  return (
    <div className="authenticator-challenge-flow" data-testid="authenticator-challenge-flow">
      <div className="mfa-challenge-header">
        <h2 className="mfa-challenge-title">{translate('authenticator.title')}</h2>
        <p className="mfa-challenge-subtitle">{translate('authenticator.subtitle')}</p>
      </div>

      {/* SAVE-FEEDBACK-001 (SAVE-FEEDBACK-CANON-001 P3): the challenge's outcome is the ONE feedback
          component — `role="alert"` is its, never hand-written `.alert` markup. The flow's column
          has no gap, so the margin that kept the alert off the number stays, on a wrapper. */}
      {message ? (
        <div className="mb-md">
          <InlineFeedback tone="error" message={message} testId="authenticator-error" />
        </div>
      ) : null}

      {codeMode ? (
        <AuthenticatorCodeEntry challengeId={challengeId} submitTotpCode={submitTotpCode} t={t} />
      ) : (
        <>
          {/* `aria-live="polite"` and not `assertive`: the number is present from first paint and
              does not change, so announcing it politely reads it once without interrupting. */}
          <p
            className="authenticator-number-match"
            data-testid="authenticator-number-match"
            aria-live="polite"
            aria-label={translate('authenticator.numberAria', { number: numberMatch })}
          >
            {numberMatch}
          </p>

          {!isResolved && (
            <p className="authenticator-status" role="status" data-testid="authenticator-waiting">
              {translate('authenticator.waiting')}
            </p>
          )}
        </>
      )}

      <AuthenticatorAltActions
        showUseCode={hasFallback && showFallback && !codeMode}
        onUseCode={handleUseCode}
        codeMode={codeMode}
        onBackToPush={handleBackToPush}
        onCancel={onCancel}
        t={t}
      />
    </div>
  );
}

AuthenticatorChallengeFlow.propTypes = {
  challengeId: PropTypes.string.isRequired,
  numberMatch: PropTypes.number.isRequired,
  pollStatus: PropTypes.func.isRequired,
  onApproved: PropTypes.func.isRequired,
  onDenied: PropTypes.func,
  onExpired: PropTypes.func,
  onUseCode: PropTypes.func,
  submitTotpCode: PropTypes.func,
  onCancel: PropTypes.func,
  t: PropTypes.func,
};
